import React, { useState } from 'react'
import { useHistory } from 'react-router'
import './SidebarAccountSwitcher.scss'
import { useAuthedUser } from 'context/AuthedUser/AuthedUserContext'
import { useUiStateContext } from 'context/UiState/UiStateContext'
import AuthCombinedApi from 'api/AuthCombinedApi'
import { useActiveMerchant } from 'components/ActiveMerchantContext'

const SidebarAccountSwitcher = (): React.ReactElement => {
    const { user, setSelectedUserId, setSelectedUsername } = useAuthedUser()
    const { invalidate } = useUiStateContext()
    const userId = user?.id ?? 0

    const history = useHistory()
    const {
        setMerchantSwitchToggle,
        userDefaultMerchant,
        id: selectedId,
        setActiveMerchant,
    } = useActiveMerchant()
    const showClientTypeIssuerUI = user?.is_issuer ?? false
    const [isSelectionVisible, setIsSelectionVisible] = useState(false)

    const toggleSelection = () => {
        setIsSelectionVisible(!isSelectionVisible)
    }

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            toggleSelection()
        }
    }

    const handleLoginAsUser = async (userId: number, username: string) => {
        if (setSelectedUserId) await setSelectedUserId(userId)
        if (setSelectedUsername) await setSelectedUsername(username)

        await invalidate('whoami')
        AuthCombinedApi.authedUser().then((userInfo) => {
            // Reset merchant switcher
            if (userDefaultMerchant === selectedId) {
                setActiveMerchant({
                    name: userInfo?.merchant?.business_name ?? '',
                    id: userInfo?.merchant?.id.toString() ?? '1',
                })
            }
            setMerchantSwitchToggle(true)

            const hasDashboardAccess = userInfo?.resources.some(
                (resource) => resource.url === '/dashboard'
            )
            hasDashboardAccess
                ? history.push('/')
                : history.push(userInfo?.resources[0].url ?? '/')

            toggleSelection()
        })
    }

    const handleBackasOriginalUser = async () => {
        if (setSelectedUserId) await setSelectedUserId(0)
        if (setSelectedUsername) await setSelectedUsername('')
        localStorage.removeItem('selectedUser')
        localStorage.removeItem('selectedUsername')

        await invalidate('whoami')

        history.push('/')
        toggleSelection()
    }

    // USER SWITCHES INCLUDE:
    // issuer <-> merchant
    // 35:sabre_admin <-> 68:sabre-partner-admin
    // 88:superadmin_user <-> 72:partneradmin_user

    const handleIssuerSwitch = () => {
        if (showClientTypeIssuerUI) return toggleSelection()

        // ISSUERS: 35:sabre_admin, 88:superadmin_user
        if (
            ['sabre_admin', 'superadmin_user'].includes(
                user?.personRepresentation() ?? ''
            )
        ) {
            return handleBackasOriginalUser()
        }
        // MERCHANT: 68:sabre-partner-admin
        if (userId === 68) {
            handleLoginAsUser(35, 'sabre_admin')
        }
        // MERCHANT: 72:partneradmin_user
        if (userId === 72) {
            handleLoginAsUser(88, 'superadmin_user')
        }
    }

    const handleMerchantSwitch = () => {
        if (!showClientTypeIssuerUI) return toggleSelection()
        // MERCHANTS: 68:sabre-partner-admin, 72:partneradmin_user
        if (
            ['sabre-partner-admin', 'partneradmin_user'].includes(
                user?.personRepresentation() ?? ''
            )
        ) {
            return handleBackasOriginalUser()
        }
        // ISSUER: 35:sabre_admin
        if (userId === 35) {
            handleLoginAsUser(68, 'sabre-partner-admin')
        }
        // ISSUER: 88:superadmin_user
        if (userId === 88) {
            handleLoginAsUser(72, 'partneradmin_user')
        }
    }

    return (
        <div className="c-sidebar-account-switcher">
            <div
                className={`c-sidebar-account-switcher__selection ${
                    !isSelectionVisible
                        ? 'c-sidebar-account-switcher__selection--hidden'
                        : ''
                }`}
            >
                <div className="c-sidebar-account-switcher__items">
                    <div className="c-sidebar-account-switcher__items-title">
                        Switch Experience
                    </div>
                    <div
                        className={`c-sidebar-account-switcher__selection-item ${
                            showClientTypeIssuerUI && '--active'
                        }`}
                        onClick={handleIssuerSwitch}
                    >
                        <div className="c-sidebar-account-switcher__selection-item-text">
                            Issuer Experience
                        </div>

                        {!showClientTypeIssuerUI && (
                            <div className="c-sidebar-account-switcher__selection-item-icon">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M15.312 11.424a5.5 5.5 0 0 1-9.201 2.466l-.312-.311h2.433a.75.75 0 0 0 0-1.5H3.989a.75.75 0 0 0-.75.75v4.242a.75.75 0 0 0 1.5 0v-2.43l.31.31a7 7 0 0 0 11.712-3.138.75.75 0 0 0-1.449-.39Zm1.23-3.723a.75.75 0 0 0 .219-.53V2.929a.75.75 0 0 0-1.5 0V5.36l-.31-.31A7 7 0 0 0 3.239 8.188a.75.75 0 1 0 1.448.389A5.5 5.5 0 0 1 13.89 6.11l.311.31h-2.432a.75.75 0 0 0 0 1.5h4.243a.75.75 0 0 0 .53-.219Z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>
                        )}
                    </div>

                    <div
                        className={`c-sidebar-account-switcher__selection-item ${
                            !showClientTypeIssuerUI && '--active'
                        }`}
                        onClick={handleMerchantSwitch}
                    >
                        <div className="c-sidebar-account-switcher__selection-item-text">
                            Merchant Experience
                        </div>

                        {showClientTypeIssuerUI && (
                            <div className="c-sidebar-account-switcher__selection-item-icon">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M15.312 11.424a5.5 5.5 0 0 1-9.201 2.466l-.312-.311h2.433a.75.75 0 0 0 0-1.5H3.989a.75.75 0 0 0-.75.75v4.242a.75.75 0 0 0 1.5 0v-2.43l.31.31a7 7 0 0 0 11.712-3.138.75.75 0 0 0-1.449-.39Zm1.23-3.723a.75.75 0 0 0 .219-.53V2.929a.75.75 0 0 0-1.5 0V5.36l-.31-.31A7 7 0 0 0 3.239 8.188a.75.75 0 1 0 1.448.389A5.5 5.5 0 0 1 13.89 6.11l.311.31h-2.432a.75.75 0 0 0 0 1.5h4.243a.75.75 0 0 0 .53-.219Z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div
                className="c-sidebar-account-switcher__button"
                onClick={toggleSelection}
                onKeyDown={handleKeyDown}
                role="button"
                tabIndex={0}
                aria-expanded={isSelectionVisible}
                aria-label="Toggle experience switcher"
            >
                <div>
                    {showClientTypeIssuerUI
                        ? 'Issuer Experience'
                        : 'Merchant Experience'}
                </div>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="c-sidebar-account-switcher__button-icon"
                >
                    <path
                        fillRule="evenodd"
                        d="M10.53 3.47a.75.75 0 0 0-1.06 0L6.22 6.72a.75.75 0 0 0 1.06 1.06L10 5.06l2.72 2.72a.75.75 0 1 0 1.06-1.06l-3.25-3.25Zm-4.31 9.81 3.25 3.25a.75.75 0 0 0 1.06 0l3.25-3.25a.75.75 0 1 0-1.06-1.06L10 14.94l-2.72-2.72a.75.75 0 0 0-1.06 1.06Z"
                        clipRule="evenodd"
                    />
                </svg>
            </div>
        </div>
    )
}

export default SidebarAccountSwitcher
